import { useCallback, useEffect, useState } from "react";
import { useFuneralHome } from "../../context/FuneralHomeContext";
import { AccountingApi } from "../../api/accountingApi";
import { StockCorrectionData } from "../../models/StockData";
import FuneralHomeHandler from "../FuneralHomeHandler";
import { useTranslation } from "react-i18next";
import { SereniApi } from "../../api/sereniApi";
import HiddenStockDropdown from "../dropdown/HiddenStockDropdown";
import { Settings } from "../../models/Settings";

function StockCorrection() {
    const { selectedAccountingEntity } = useFuneralHome();
    const { t } = useTranslation();

    const [stockCorrectionOverview, setStockCorrectionOverview] = useState<
        StockCorrectionData[]
    >([]);
    const [filteredStockCorrection, setFilteredStockCorrection] = useState<
        StockCorrectionData[]
    >([]);
    const [hiddenStockItems, setHiddenStockItems] = useState<
        StockCorrectionData[]
    >([]);
    const [selectedHiddenItem, setSelectedHiddenItem] = useState<string>("");
    const [manuallyAddedItems, setManuallyAddedItems] = useState<Set<string>>(
        new Set(),
    );

    const [settings, setSettings] = useState<Settings | null>(null);

    const [isLoadingStock, setIsLoadingStock] = useState<boolean>(false);
    const [errorStock, setErrorStock] = useState<string | null>(null);

    const [isLoadingFloatingStock, setIsLoadingFloatingStock] =
        useState<boolean>(false);
    const [errorFloatingStock, setErrorFloatingStock] = useState<string | null>(
        null,
    );

    const [errorCorrection, setErrorCorrection] = useState<string | null>(null);

    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [showBlockedWarning, setShowBlockedWarning] = useState<boolean>(false);

    // Helper function to filter stock items
    const filterStockItems = (stockData: StockCorrectionData[]) => {
        return stockData.filter(
            (item) =>
                item.inventory !== 0 ||
                (item.toBeInvoicedQuantity && item.toBeInvoicedQuantity > 0),
        );
    };

    const fetchSettings = useCallback(() => {
        SereniApi.fetchSettings()
            .then((data) => {
                setSettings(data);
            })
            .catch((error) => {
                console.error("Error fetching settings:", error);
            });
    }, []);

    const fetchStockOverview = useCallback(() => {
        if (!selectedAccountingEntity) return;

        setIsLoadingStock(true);
        setStockCorrectionOverview([]);
        setManuallyAddedItems(new Set()); // Reset manually added items
        setErrorStock(null);
        AccountingApi.fetchStockOverview(
            selectedAccountingEntity.COMPANY_ID,
            selectedAccountingEntity.COSTCODE,
        )
            .then((data) => {
                // Filter to only include items with itemCategoryCode 'COFFIN'
                const filteredData = data.filter(
                    (item: StockCorrectionData) =>
                        item.itemCategoryCode === "COFFIN",
                );
                setStockCorrectionOverview(filteredData);
                fetchFloatingStockWithData(filteredData);
            })
            .catch((error) => {
                let errorMessage = error.message;
                if (error.response?.data.ERROR) {
                    errorMessage = error.response.data.ERROR;
                }
                setErrorStock(errorMessage);
            })
            .finally(() => {
                setIsLoadingStock(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccountingEntity]);

    const handleCorrection = () => {
        if (!selectedAccountingEntity) {
            return;
        }

        setIsProcessing(true);
        setErrorCorrection(null);
        AccountingApi.createStockCorrection(
            selectedAccountingEntity.COMPANY_ID,
            selectedAccountingEntity.COSTCODE,
            filteredStockCorrection, // Use filtered data instead of full overview
        )
            .then((data) => {})
            .catch((error) => {
                let errorMessage = error.message;
                if (error.response?.data.ERROR) {
                    errorMessage = error.response.data.ERROR;
                }
                setErrorCorrection(errorMessage);
            })
            .finally(() => {
                fetchStockOverview();
                setIsProcessing(false);
            });
    };

    const handleAddHiddenItem = () => {
        if (!selectedHiddenItem) return;

        // Find the selected item in hidden items
        const itemToAdd = hiddenStockItems.find(
            (item) => item.id === selectedHiddenItem,
        );
        if (!itemToAdd || itemToAdd.blocked) return;

        // Add to filtered list
        setFilteredStockCorrection((prev) => [...prev, itemToAdd]);

        // Track this item as manually added
        setManuallyAddedItems((prev) => new Set(prev).add(selectedHiddenItem));

        // Remove from hidden items
        setHiddenStockItems((prev) =>
            prev.filter((item) => item.id !== selectedHiddenItem),
        );

        // Reset selection
        setSelectedHiddenItem("");
    };

    const fetchFloatingStockWithData = (stockData: StockCorrectionData[]) => {
        if (!selectedAccountingEntity || stockData.length === 0) return;

        setIsLoadingFloatingStock(true);
        setErrorFloatingStock(null);
        SereniApi.fetchFloatingStock(
            selectedAccountingEntity.COSTCODE,
            stockData,
        )
            .then((data) => {
                const updatedStockCorrection = stockData.map((item) => {
                    const floatingItem = data.find(
                        (floatingItem) => floatingItem.CODE === item.no,
                    );
                    const toBeInvoicedQuantity = floatingItem
                        ? floatingItem.TOTAL
                        : 0;

                    return {
                        ...item,
                        correctionQuantity:
                            item.inventory - toBeInvoicedQuantity,
                        toBeInvoicedQuantity: toBeInvoicedQuantity,
                    };
                });
                setStockCorrectionOverview(updatedStockCorrection);

                // Filter items to only show those with inventory !== 0 or toBeInvoicedQuantity > 0
                const filtered = filterStockItems(updatedStockCorrection);
                setFilteredStockCorrection(filtered);

                // Set hidden items (items not in the filtered list)
                const hidden = updatedStockCorrection.filter(
                    (item) =>
                        !filtered.find(
                            (filteredItem) => filteredItem.id === item.id,
                        ),
                );
                setHiddenStockItems(hidden);
            })
            .catch((error) => {
                let errorMessage = error.message;
                if (error.response?.data.ERROR) {
                    errorMessage = error.response.data.ERROR;
                }
                setErrorFloatingStock(errorMessage);
            })
            .finally(() => {
                setIsLoadingFloatingStock(false);
            });
    };

    const handleQuantityChange = (id: string, value: number) => {
        // Also update the filtered data
        const updatedFilteredData = filteredStockCorrection.map((item) =>
            item.id === id ? { ...item, correctionQuantity: value } : item,
        );
        setFilteredStockCorrection(updatedFilteredData);
    };



    const handleRemoveItem = (id: string) => {
        // Only allow removal of manually added items
        if (!manuallyAddedItems.has(id)) return;

        // Find the item to remove
        const itemToRemove = filteredStockCorrection.find(
            (item) => item.id === id,
        );
        if (!itemToRemove) return;

        // Reset its correction quantity to the default
        const resetItem = {
            ...itemToRemove,
            correctionQuantity:
                itemToRemove.inventory -
                (itemToRemove.toBeInvoicedQuantity || 0),
        };

        // Remove from filtered list
        setFilteredStockCorrection((prev) =>
            prev.filter((item) => item.id !== id),
        );

        // Remove from manually added items tracking
        setManuallyAddedItems((prev) => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });

        // Add back to hidden items (since it was manually added, it should go back to hidden)
        setHiddenStockItems((prev) => [...prev, resetItem]);

        // Update the main stock overview
        setStockCorrectionOverview((prev) =>
            prev.map((item) => (item.id === id ? resetItem : item)),
        );
    };

    // Effect to run once on component mount to fetch settings
    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Effect to run when the component mounts or when selectedAccountingEntity changes
    useEffect(() => {
        fetchStockOverview(); // Fetch data
    }, [fetchStockOverview, selectedAccountingEntity]); // Dependency array

    return (
        <div className="container mt-5">
            <h1>{t("correction.title")}</h1>
            <div className="alert alert-info" role="alert">
                <strong>ℹ️ {t("correction.how_to_use")}</strong>
                <ol className="mb-0 mt-2">
                    <li>{t("correction.step1")}</li>
                    <li>{t("correction.step2")}</li>
                    <li>{t("correction.step3")}</li>
                    <li>
                        <strong>{t("correction.step4")}</strong>
                    </li>
                </ol>
            </div>
            <p>
                {t("correction.information")} <br />
                {t("correction.information_1")}
            </p>

            <FuneralHomeHandler />

            {/* Display Stock Overview Data */}
            {selectedAccountingEntity != null && (
                <div className="mt-4">
                    {!isLoadingStock &&
                        !isLoadingFloatingStock &&
                        filteredStockCorrection.length === 0 &&
                        stockCorrectionOverview.length > 0 && (
                            <p className="text-info">
                                {t("correction.no_stock_to_display")}
                            </p>
                        )}
                    {!isLoadingStock &&
                        !isLoadingFloatingStock &&
                        stockCorrectionOverview.length === 0 && (
                            <p className="text-info">
                                {t("correction.no_stock")}
                            </p>
                        )}
                    {/* Loading and Error States */}
                    {(isLoadingStock || isLoadingFloatingStock) && (
                        <p className="text-info">{t("loading.stock")}</p>
                    )}
                    {filteredStockCorrection.length > 0 && (
                        <>
                            <table className="mt-4 table table-striped">
                                <thead>
                                    <tr>
                                        <th>
                                            {t(
                                                "stockoverview.table.sereninumber",
                                            )}
                                        </th>
                                        <th>
                                            {t(
                                                "stockoverview.table.suppliernumber",
                                            )}
                                        </th>
                                        <th>
                                            {t(
                                                "stockoverview.table.description",
                                            )}
                                        </th>
                                        <th>
                                            {t("stockoverview.table.category")}
                                        </th>
                                        <th>
                                            {t("stockoverview.table.amount")}
                                        </th>
                                        <th>
                                            {t(
                                                "stockoverview.table.amountnotinvoiced",
                                            )}
                                            {settings?.LAST_EXPORT_DATE && (
                                                <div
                                                    className="text-muted"
                                                    style={{
                                                        fontSize: "0.8em",
                                                        fontWeight: "normal",
                                                    }}
                                                >
                                                    (
                                                    {t(
                                                        "stockoverview.table.lastcount",
                                                    )}
                                                    :{" "}
                                                    {new Date(
                                                        settings.LAST_EXPORT_DATE,
                                                    ).toLocaleDateString()}
                                                    )
                                                </div>
                                            )}
                                        </th>
                                        <th>
                                            {t("stockoverview.table.quantity")}
                                        </th>
                                        <th>
                                            {t("stockoverview.table.actions")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStockCorrection.map((item) => (
                                        <tr key={item.id}>
                                            <td>{item.no}</td>
                                            <td>{item.no2}</td>
                                            <td>{item.description}</td>
                                            <td>
                                                {t(
                                                    "productcategory." +
                                                        item.itemCategoryCode,
                                                )}
                                            </td>
                                            <td>{item.inventory}</td>
                                            <td>{item.toBeInvoicedQuantity}</td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="number"
                                                        className="form-control me-2"
                                                        placeholder={t(
                                                            "purchaseorder.table.quantity",
                                                        )}
                                                        min={0}
                                                        value={
                                                            item.correctionQuantity
                                                        }
                                                        onChange={(e) =>
                                                            handleQuantityChange(
                                                                item.id,
                                                                +e.target.value,
                                                            )
                                                        }
                                                        disabled={item.blocked}
                                                    />
                                                    {item.blocked && (
                                                        <span
                                                            className="text-warning fs-5"
                                                            onClick={() => setShowBlockedWarning(true)}
                                                            style={{ cursor: "pointer", flexShrink: 0 }}
                                                        >
                                                            ⚠️
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {item.blocked ? (
                                                    <span
                                                        className="badge text-bg-warning"
                                                        onClick={() => setShowBlockedWarning(true)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        {t("correction.blocked")}
                                                    </span>
                                                ) : manuallyAddedItems.has(
                                                    item.id,
                                                ) ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() =>
                                                            handleRemoveItem(
                                                                item.id,
                                                            )
                                                        }
                                                        title={t(
                                                            "correction.remove_item",
                                                        )}
                                                    >
                                                        ×
                                                    </button>
                                                ) : (
                                                    <span className="text-muted">
                                                        -
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Add hidden items section */}
                            {hiddenStockItems.length > 0 && (
                                <div className="mt-3 p-3 border rounded bg-light">
                                    <h5>{t("correction.add_item")}</h5>
                                    <div className="row">
                                        <div className="col-md-8">
                                            <HiddenStockDropdown
                                                selectedItem={
                                                    selectedHiddenItem
                                                }
                                                handleItemChange={(itemId) =>
                                                    setSelectedHiddenItem(
                                                        itemId || "",
                                                    )
                                                }
                                                hiddenItems={hiddenStockItems}
                                            />
                                        </div>
                                        <div className="col-md-4">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                onClick={handleAddHiddenItem}
                                                disabled={!selectedHiddenItem}
                                            >
                                                {t("correction.add_selected")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mt-4 mb-5">
                                {errorStock && (
                                    <div
                                        className="alert alert-danger"
                                        role="alert"
                                    >
                                        <strong>
                                            ⚠️{" "}
                                            {t("error.stock", {
                                                message: errorStock,
                                            })}
                                        </strong>
                                    </div>
                                )}
                                {isProcessing && (
                                    <div
                                        className="alert alert-info"
                                        role="alert"
                                    >
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                        ></span>
                                        {t("loading.request")}
                                    </div>
                                )}

                                {errorCorrection && (
                                    <div
                                        className="alert alert-danger"
                                        role="alert"
                                    >
                                        <strong>
                                            ⚠️{" "}
                                            {t("error.correction", {
                                                message: errorCorrection,
                                            })}
                                        </strong>
                                    </div>
                                )}
                                {errorFloatingStock && (
                                    <div
                                        className="alert alert-danger"
                                        role="alert"
                                    >
                                        <strong>
                                            ⚠️{" "}
                                            {t("error.floatingStock", {
                                                message: errorFloatingStock,
                                            })}
                                        </strong>
                                    </div>
                                )}

                                {/* Sticky save section */}
                                {filteredStockCorrection.length > 0 && (
                                    <div
                                        className="position-sticky bottom-0 bg-white border-top shadow-lg p-3 mt-4"
                                        style={{ zIndex: 1000 }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="text-muted">
                                                <small>
                                                    💡{" "}
                                                    {t(
                                                        "correction.remember_to_save",
                                                    )}
                                                </small>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-lg"
                                                onClick={handleCorrection}
                                                disabled={isProcessing}
                                                style={{ minWidth: "200px" }}
                                            >
                                                {isProcessing ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm me-2"
                                                            role="status"
                                                        ></span>
                                                        {t("loading.request")}
                                                    </>
                                                ) : (
                                                    <>
                                                        💾{" "}
                                                        {t("correction.submit")}
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        {showBlockedWarning && (
            <>
                <div
                    className="modal-backdrop fade show"
                    onClick={() => setShowBlockedWarning(false)}
                />
                <div
                    className="modal fade show d-block"
                    tabIndex={-1}
                    onClick={() => setShowBlockedWarning(false)}
                >
                    <div
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    ⚠️ {t("correction.blocked")}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowBlockedWarning(false)}
                                />
                            </div>
                            <div className="modal-body">
                                {t("correction.blocked_warning")}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowBlockedWarning(false)}
                                >
                                    {t("general.close")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )}
        </div>
    );
}
export default StockCorrection;
